// Copied from 01.fleziLOG/client/src/lib/keycloak.ts (same identity provider, same constraints).
// ---------------------------------------------------------------------------
// Polyfill: Web Crypto APIs for non-secure contexts (plain HTTP).
// keycloak-js needs: crypto.randomUUID, crypto.getRandomValues, crypto.subtle.digest
// Only randomUUID and subtle are blocked on HTTP — getRandomValues works everywhere.
// Must run BEFORE importing keycloak-js.
// ---------------------------------------------------------------------------

interface CryptoPolyfillTarget {
  crypto: {
    randomUUID?: () => string;
    getRandomValues?: (array: Uint8Array) => Uint8Array;
    subtle?: {
      digest: (
        algorithm: string | { name: string },
        data: ArrayBuffer,
      ) => Promise<ArrayBuffer>;
    };
  };
}

const g = globalThis as unknown as CryptoPolyfillTarget;

if (typeof g.crypto === "undefined") {
  g.crypto = {};
}

// 1. crypto.randomUUID — generates state/nonce for OIDC flow
if (typeof g.crypto.randomUUID !== "function") {
  g.crypto.randomUUID = (): string => {
    const bytes = new Uint8Array(16);
    if (typeof g.crypto.getRandomValues !== "function") {
      // Refuse rather than degrade. This UUID becomes the PKCE state/nonce, so a
      // Math.random() fallback would make it predictable — an attacker who can
      // guess it can complete an authorization-code interception. getRandomValues
      // is available even in insecure contexts (unlike crypto.randomUUID, which is
      // why this polyfill exists at all), so there is no legitimate path here.
      throw new Error("No cryptographically secure RNG available — refusing to mint a PKCE nonce");
    }
    g.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6]! & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8]! & 0x3f) | 0x80; // variant 1
    const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  };
}

// 2. crypto.subtle.digest — SHA-256 for PKCE S256 code challenge
if (!g.crypto.subtle) {
  // Pure-JS SHA-256 implementation for non-secure contexts
  const sha256 = async (data: ArrayBuffer): Promise<ArrayBuffer> => {
    const bytes = new Uint8Array(data);
    const K = [
      0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
      0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
      0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
      0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
      0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
      0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
      0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
      0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
      0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
      0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
      0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
    ];

    const rotr = (n: number, x: number) => (x >>> n) | (x << (32 - n));
    const ch = (x: number, y: number, z: number) => (x & y) ^ (~x & z);
    const maj = (x: number, y: number, z: number) => (x & y) ^ (x & z) ^ (y & z);
    const sigma0 = (x: number) => rotr(2, x) ^ rotr(13, x) ^ rotr(22, x);
    const sigma1 = (x: number) => rotr(6, x) ^ rotr(11, x) ^ rotr(25, x);
    const gamma0 = (x: number) => rotr(7, x) ^ rotr(18, x) ^ (x >>> 3);
    const gamma1 = (x: number) => rotr(17, x) ^ rotr(19, x) ^ (x >>> 10);

    // Pre-processing: padding
    const msgLen = bytes.length;
    const bitLen = msgLen * 8;
    const padded = new Uint8Array(Math.ceil((msgLen + 9) / 64) * 64);
    padded.set(bytes);
    padded[msgLen] = 0x80;
    const view = new DataView(padded.buffer);
    view.setUint32(padded.length - 4, bitLen, false);

    let h0 = 0x6a09e667, h1 = 0xbb67ae85, h2 = 0x3c6ef372, h3 = 0xa54ff53a;
    let h4 = 0x510e527f, h5 = 0x9b05688c, h6 = 0x1f83d9ab, h7 = 0x5be0cd19;

    for (let offset = 0; offset < padded.length; offset += 64) {
      const w = new Array(64);
      for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4, false);
      for (let i = 16; i < 64; i++)
        w[i] = (gamma1(w[i - 2]) + w[i - 7] + gamma0(w[i - 15]) + w[i - 16]) | 0;

      let a = h0, b = h1, c = h2, d = h3, e = h4, f = h5, g2 = h6, h = h7;
      for (let i = 0; i < 64; i++) {
        const t1 = (h + sigma1(e) + ch(e, f, g2) + K[i]! + w[i]) | 0;
        const t2 = (sigma0(a) + maj(a, b, c)) | 0;
        h = g2; g2 = f; f = e; e = (d + t1) | 0;
        d = c; c = b; b = a; a = (t1 + t2) | 0;
      }

      h0 = (h0 + a) | 0; h1 = (h1 + b) | 0; h2 = (h2 + c) | 0; h3 = (h3 + d) | 0;
      h4 = (h4 + e) | 0; h5 = (h5 + f) | 0; h6 = (h6 + g2) | 0; h7 = (h7 + h) | 0;
    }

    const result = new ArrayBuffer(32);
    const resultView = new DataView(result);
    [h0, h1, h2, h3, h4, h5, h6, h7].forEach((v, i) => resultView.setUint32(i * 4, v, false));
    return result;
  };

  g.crypto.subtle = {
    digest(algorithm: string | { name: string }, data: ArrayBuffer): Promise<ArrayBuffer> {
      const alg = typeof algorithm === "string" ? algorithm : algorithm?.name;
      if (alg === "SHA-256") return sha256(data);
      return Promise.reject(new Error(`Unsupported algorithm: ${alg}`));
    },
  };
}

export {};
